import { useEffect, useState } from "react";
import { Basket, Package, DownloadSimple, MagnifyingGlass, Tag, Sparkle } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { money } from "@/lib/format";
import { Card, Loading, catColor } from "@/components/riq";
import SortableTable from "@/components/SortableTable";
import { useAdminNav } from "@/pages/adminNav";
import { toast } from "sonner";

const PERIODS = [
  { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }, { k: "90d", l: "90 days" }, { k: "all", l: "All time" },
];
const zar = (v) => money(v, "ZAR");

export default function AdminProducts() {
  const { openReceipts } = useAdminNav();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    setData(null);
    api.get(`/admin/reports?period=${period}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [period]);
  useEffect(() => { api.get("/admin/brands").then((r) => setBrands(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/smart-shopper-readiness").then((r) => setReadiness(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    const token = localStorage.getItem("riq_token");
    try {
      const res = await fetch(`${API}/admin/reports/export?section=top_products&period=${period}`, {
        credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sliply_products_${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export CSV."); }
  };

  if (data === false) return <div className="text-muted-foreground">Could not load products.</div>;

  const products = (data?.top_products || []).filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  const cats = data?.by_category || [];
  const maxCat = Math.max(1, ...cats.map((c) => c.gross));

  return (
    <div className="space-y-5" data-testid="admin-products">
      {readiness && <SmartShopperCard r={readiness} />}

      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button key={p.k} data-testid={`products-period-${p.k}`} onClick={() => setPeriod(p.k)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${period === p.k ? "bg-riq-primary text-white" : "border border-riq-border text-neutral-600 hover:border-riq-primary"}`}>
            {p.l}
          </button>
        ))}
      </div>

      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="products-top">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2"><Package size={18} weight="duotone" /> Top products</h3>
                <button onClick={exportCsv} data-testid="products-export" className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs font-medium hover:border-riq-primary text-neutral-600">
                  <DownloadSimple size={14} weight="bold" /> CSV
                </button>
              </div>
              <div className="relative mb-3">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input data-testid="products-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter products…" className="riq-input !pl-9 !py-2 w-full text-sm" />
              </div>
              {products.length ? (
                <SortableTable
                  columns={[
                    { key: "name", label: "Product", type: "text", render: (r) => (
                      <button data-testid={`product-name-link-${r.name}`} onClick={() => openReceipts({ product: r.name })} className="font-medium text-riq-primary hover:underline text-left truncate max-w-[240px] block" title="Show receipts with this product">{r.name}</button>
                    ) },
                    { key: "qty", label: "Qty", type: "number", align: "right" },
                    { key: "receipts", label: "Rcpts", type: "number", align: "right" },
                    { key: "gross", label: "Gross", type: "number", align: "right", render: (r) => <span className="font-semibold">{zar(r.gross)}</span> },
                  ]}
                  rows={products}
                  defaultSort={[{ key: "gross", dir: "desc" }]}
                  rowKey="name"
                  testIdPrefix="product-row"
                  empty="No products."
                />
              ) : <Empty />}
            </Card>

            <div className="space-y-4">
              <Card data-testid="products-categories">
                <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4"><Basket size={18} weight="duotone" /> Spend by category</h3>
                {cats.length ? (
                  <div className="space-y-2.5">
                    {cats.slice(0, 10).map((r, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate flex-1 font-medium">{r.label}</span>
                          <span className="text-muted-foreground text-xs mx-2">{r.count}</span>
                          <span className="font-semibold w-24 text-right">{zar(r.gross)}</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(r.gross / maxCat) * 100}%`, background: catColor(r.label) }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <Empty />}
              </Card>

              <Card data-testid="products-brands">
                <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4"><Tag size={18} weight="duotone" /> Top brands</h3>
                {brands.length ? (
                  <div className="divide-y divide-riq-border -mx-1">
                    {brands.slice(0, 10).map((b, i) => (
                      <button key={i} onClick={() => openReceipts({ brand: b.name })} className="w-full flex items-center px-1 py-2 text-sm text-left hover:bg-secondary/40 rounded-lg transition-colors">
                        <span className="flex-1 min-w-0 truncate font-medium text-riq-primary hover:underline">{b.name}</span>
                        <span className="text-muted-foreground text-xs mx-2">{b.merchants} stores</span>
                        <span className="font-semibold w-24 text-right">{zar(b.gross)}</span>
                      </button>
                    ))}
                  </div>
                ) : <Empty />}
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-muted-foreground py-6 text-center">No data for this period.</div>;
}

function SmartShopperCard({ r }) {
  const pct = r.percent || 0;
  const tipping = pct >= 40 || r.ready_products >= 20;
  return (
    <Card data-testid="smart-shopper-readiness" className="!p-0 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5">
        <div className="flex items-center gap-3 flex-1">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tipping ? "bg-emerald-100 text-emerald-600" : "bg-riq-primary/10 text-riq-primary"}`}>
            <Sparkle size={22} weight="duotone" />
          </div>
          <div>
            <div className="font-head font-bold text-riq-primary">Smart Shopper readiness</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Products with ≥{r.threshold.price_points} prices across ≥{r.threshold.merchants} merchants — enough to compare & find the cheapest store.
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-head text-3xl font-bold ${tipping ? "text-emerald-600" : "text-riq-primary"}`}>{r.ready_products}<span className="text-lg text-muted-foreground font-semibold"> / {r.total_products}</span></div>
          <div className="text-xs text-muted-foreground">{pct}% ready · {r.almost_ready} almost there</div>
        </div>
      </div>
      <div className="h-2 bg-secondary">
        <div className={`h-full ${tipping ? "bg-emerald-500" : "bg-riq-primary"}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="px-5 py-2.5 text-xs text-muted-foreground bg-secondary/30">
        {tipping
          ? "✅ You've hit the tipping point — Smart Shopper price insights would be genuinely useful now."
          : "Keep users scanning — Smart Shopper unlocks well once your top products each have prices from a couple of stores."}
      </div>
    </Card>
  );
}
